import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { Autocomplete, Box, Button, Grid2, Stack, Typography } from '@mui/material'
import TextField from '@mui/material/TextField'
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Option } from 'src/shared/form/control'
import { CONFIGURATION_STATUS, PROCESS } from 'src/shared/constants/constants'
import { EvaluationCriteriaGrid } from 'src/screens/administration/configuration-management/criteria-configuration/evaluation-criteria/EvaluationCriteriaGrid'
import { Application_Type, Criteria_Config_Bool_Exp, Criteria_Config_Order_By, Query_Root } from 'src/@types/graphql'
import { useClient } from 'urql'
import { queryCriteriaConfigs } from 'src/screens/administration/configuration-management/criteria-configuration/criteriaConfigQueries'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useUserLocale } from 'src/user/UserContext'
import {
  deleteEvaluationCriteria,
  fetchApplicationTypesByProcesses,
  getProcessFilterOptionForApplicationType,
  mapApplicationTypesToOptions,
  shouldResetApplicationTypeFilterAfterProcessFilterChange,
} from 'src/screens/administration/configuration-management/criteria-configuration/evaluation-criteria/EvaluationCriteriaGridUtils'
import {
  CRITERIA_CONFIG_PROCESSES,
  getCriteriaConfigProcessOptions,
  getCriteriaTypeOptions,
} from 'src/screens/administration/configuration-management/criteria-configuration/CriteriaConfigUtils'
import {
  ConfigurationGridDataSettings,
  ConfigurationTablePageSortSettings,
  DEFAULT_CONFIG_GRID_DATA_SETTINGS,
  DEFAULT_CONFIG_TABLE_PAGE_SORT_SETTINGS,
} from 'src/shared/constants/table-settings-constants'
import {
  SpecificConfigFilter,
  useFilterSettingsFromLocalStorage,
  useTableSettingsFromLocalStorage,
} from 'src/screens/administration/configuration-management/ConfigurationStorageUtils'
import {
  getConfigStatusOptions,
  getOptionsForFilter,
  getTranslationForApplicationType,
  getTranslationForCriteriaType,
  getTranslationForProcess,
  getTranslationForStatus,
} from 'src/screens/administration/configuration-management/ConfigurationUtils'
import { SecondaryConfirmationModalDialog } from 'src/shared/modal-dialog/SecondaryConfirmationModalDialog'

const EVALUATION_CRITERIA_TABLE_NAME = 'EVALUATION_CRITERIA'

type EvaluationCriteriaFilters = SpecificConfigFilter<'EVALUATION_CRITERIA'>

const defaultEvaluationCriteriaTableSettings: ConfigurationTablePageSortSettings = {
  ...DEFAULT_CONFIG_TABLE_PAGE_SORT_SETTINGS,
  sortModel: [{ field: 'criteria_id', sort: 'asc' }],
}

export const CriteriaConfigurationEvaluationCriteriaPage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const locale = useUserLocale()

  // fetched filter data - application types
  const [allApplicationTypes, setAllApplicationTypes] = useState<Application_Type[]>([])

  // filter options
  const criteriaProcessOptions: Option[] = useMemo(
    () => getOptionsForFilter(getCriteriaConfigProcessOptions(getMessage)),
    [getMessage],
  )

  const getCriteriaApplicationTypeOptions = useCallback(
    (processFilter: Option | null): Option[] => {
      if (processFilter?.value != null) {
        const processOptions = mapApplicationTypesToOptions(
          allApplicationTypes.filter((aType) => aType.process === processFilter.value),
        )
        return getOptionsForFilter(processOptions)
      } else {
        const allApplicationTypeOptions = mapApplicationTypesToOptions(allApplicationTypes)
        return getOptionsForFilter(allApplicationTypeOptions)
      }
    },
    [allApplicationTypes],
  )

  const criteriaTypeOptions: Option[] = useMemo(
    () => getOptionsForFilter(getCriteriaTypeOptions(getMessage)),
    [getMessage],
  )
  const criteriaStatusOptions: Option[] = useMemo(
    () => getOptionsForFilter(getConfigStatusOptions(getMessage)),
    [getMessage],
  )

  const defaultEvaluationCriteriaFiltersValues: EvaluationCriteriaFilters = useMemo(
    () => ({
      criteriaProcessFilter: criteriaProcessOptions[0],
      criteriaApplicationTypeFilter: getCriteriaApplicationTypeOptions(null)[0],
      criteriaTypeFilter: criteriaTypeOptions[0],
      criteriaStatusFilter: criteriaStatusOptions[0],
    }),
    [criteriaProcessOptions, criteriaStatusOptions, criteriaTypeOptions, getCriteriaApplicationTypeOptions],
  )

  // grid and filter settings
  const [gridDataSettings, setGridDataSettings] = useState<ConfigurationGridDataSettings>(
    DEFAULT_CONFIG_GRID_DATA_SETTINGS,
  )
  const [tableSettings, setTableSettings] = useTableSettingsFromLocalStorage(
    EVALUATION_CRITERIA_TABLE_NAME,
    defaultEvaluationCriteriaTableSettings,
  )
  const [evaluationCriteriaFilters, setEvaluationCriteriaFilters] = useFilterSettingsFromLocalStorage(
    defaultEvaluationCriteriaFiltersValues,
  )

  const currentApplicationTypeOptions = getCriteriaApplicationTypeOptions(
    evaluationCriteriaFilters.criteriaProcessFilter,
  )

  // delete modal state
  const [deleteModalLoading, setDeleteModalLoading] = useState(false)
  const [criteriaToBeDeleted, setCriteriaToBeDeleted] = useState<number | null>(null)

  // table state
  const fetchRows = useCallback(async () => {
    const limit = tableSettings.paginationModel.pageSize
    const offset = tableSettings.paginationModel.page * tableSettings.paginationModel.pageSize

    const criteriaIdSortValue = tableSettings.sortModel?.find((sortItem) => sortItem.field === 'criteria_id')?.sort
    const orderBy = [
      {
        criteria_id: criteriaIdSortValue,
      },
      {
        id: 'asc',
      },
    ] as Array<Criteria_Config_Order_By>

    const criteriaConfigCondition: Criteria_Config_Bool_Exp = { _and: [] }
    if (evaluationCriteriaFilters.criteriaProcessFilter.value != null) {
      const selectedProcess = evaluationCriteriaFilters.criteriaProcessFilter.value as string
      criteriaConfigCondition._and?.push({ processes: { _contains: selectedProcess } })
    }
    if (evaluationCriteriaFilters.criteriaApplicationTypeFilter.value != null) {
      const applicationTypeId = evaluationCriteriaFilters.criteriaApplicationTypeFilter.value
      criteriaConfigCondition._and?.push({ application_type_ids: { _contains: applicationTypeId } })
    }
    if (evaluationCriteriaFilters.criteriaTypeFilter.value != null) {
      const selectedCriteriaType = evaluationCriteriaFilters.criteriaTypeFilter.value as string
      criteriaConfigCondition._and?.push({ criteria_type: { _eq: selectedCriteriaType } })
    }
    if (evaluationCriteriaFilters.criteriaStatusFilter.value != null) {
      const isActive = evaluationCriteriaFilters.criteriaStatusFilter.value == CONFIGURATION_STATUS.ACTIVE
      criteriaConfigCondition._and?.push({ active: { _eq: isActive } })
    }

    const { data, error } = await urqlClient
      .query<
        {
          criteria_config: Query_Root['criteria_config']
          criteria_config_aggregate: Query_Root['criteria_config_aggregate']
        },
        {
          criteriaConfigCondition: Criteria_Config_Bool_Exp
          offset: number
          limit: number
          orderBy: Criteria_Config_Order_By | Criteria_Config_Order_By[]
        }
      >(queryCriteriaConfigs, {
        criteriaConfigCondition,
        offset,
        limit,
        orderBy,
      })
      .toPromise()

    if (error || !data?.criteria_config || !data?.criteria_config_aggregate) {
      throw new Error('Resource not found')
    }

    const fetchedRows = data.criteria_config.map((cg) => ({
      id: cg.id,
      criteria_id: cg.criteria_id,
      description: cg.description[locale],
      criteria_type: cg.criteria_type,
      allocation: cg.processes?.[0] ?? undefined,
      status: cg.active,
      is_deletable: !!cg.criteria_deletability?.is_deletable,
    }))
    const fetchedRowsCount = data.criteria_config_aggregate.aggregate?.count ?? 0

    return {
      fetchedRows,
      fetchedRowsCount,
    }
  }, [tableSettings, evaluationCriteriaFilters, locale, urqlClient])

  const initRows = useCallback(() => {
    setGridDataSettings((prevGridData) => ({
      ...prevGridData,
      gridLoading: true,
    }))

    fetchRows()
      .then((res) => {
        setGridDataSettings({
          rows: res.fetchedRows,
          rowsCount: res.fetchedRowsCount,
          gridLoading: false,
        })
      })
      .catch((_) => {
        notificationService.operationFailed()
        setGridDataSettings((prevGridData) => ({
          ...prevGridData,
          gridLoading: false,
        }))
      })
  }, [fetchRows, notificationService])

  // filter handlers
  const onProcessFilterChange = useCallback(
    (newValue: Option | null) => {
      setTableSettings({
        ...tableSettings,
        paginationModel: { ...tableSettings.paginationModel, page: 0 },
      })

      const newProcessOption = newValue ?? defaultEvaluationCriteriaFiltersValues.criteriaProcessFilter

      if (
        shouldResetApplicationTypeFilterAfterProcessFilterChange(
          newValue,
          evaluationCriteriaFilters.criteriaApplicationTypeFilter,
          allApplicationTypes,
        )
      ) {
        setEvaluationCriteriaFilters({
          ...evaluationCriteriaFilters,
          criteriaProcessFilter: newProcessOption,
          criteriaApplicationTypeFilter: defaultEvaluationCriteriaFiltersValues.criteriaApplicationTypeFilter,
        })
      } else {
        setEvaluationCriteriaFilters({
          ...evaluationCriteriaFilters,
          criteriaProcessFilter: newProcessOption,
        })
      }
    },
    [
      tableSettings,
      evaluationCriteriaFilters,
      allApplicationTypes,
      defaultEvaluationCriteriaFiltersValues.criteriaProcessFilter,
      defaultEvaluationCriteriaFiltersValues.criteriaApplicationTypeFilter,
      setTableSettings,
      setEvaluationCriteriaFilters,
    ],
  )

  const onApplicationTypeFilterChange = useCallback(
    (newValue: Option | null) => {
      setTableSettings({
        ...tableSettings,
        paginationModel: { ...tableSettings.paginationModel, page: 0 },
      })

      if (newValue?.value != null) {
        const selectedType = allApplicationTypes.find((aType) => aType.id === newValue.value) as Application_Type
        const newProcessFilterValue = getProcessFilterOptionForApplicationType(selectedType, criteriaProcessOptions)

        setEvaluationCriteriaFilters({
          ...evaluationCriteriaFilters,
          criteriaApplicationTypeFilter: newValue,
          criteriaProcessFilter: newProcessFilterValue,
        })
      } else {
        setEvaluationCriteriaFilters({
          ...evaluationCriteriaFilters,
          criteriaApplicationTypeFilter: defaultEvaluationCriteriaFiltersValues.criteriaApplicationTypeFilter,
        })
      }
    },
    [
      tableSettings,
      evaluationCriteriaFilters,
      allApplicationTypes,
      criteriaProcessOptions,
      defaultEvaluationCriteriaFiltersValues.criteriaApplicationTypeFilter,
      setTableSettings,
      setEvaluationCriteriaFilters,
    ],
  )

  const onCriteriaTypeFilterChange = useCallback(
    (newValue: Option | null) => {
      setTableSettings({
        ...tableSettings,
        paginationModel: { ...tableSettings.paginationModel, page: 0 },
      })

      setEvaluationCriteriaFilters({
        ...evaluationCriteriaFilters,
        criteriaTypeFilter: newValue ?? defaultEvaluationCriteriaFiltersValues.criteriaTypeFilter,
      })
    },
    [
      tableSettings,
      evaluationCriteriaFilters,
      defaultEvaluationCriteriaFiltersValues.criteriaTypeFilter,
      setTableSettings,
      setEvaluationCriteriaFilters,
    ],
  )

  const onCriteriaStatusChange = useCallback(
    (newValue: Option | null) => {
      setTableSettings({
        ...tableSettings,
        paginationModel: { ...tableSettings.paginationModel, page: 0 },
      })

      setEvaluationCriteriaFilters({
        ...evaluationCriteriaFilters,
        criteriaStatusFilter: newValue ?? defaultEvaluationCriteriaFiltersValues.criteriaStatusFilter,
      })
    },
    [
      tableSettings,
      evaluationCriteriaFilters,
      defaultEvaluationCriteriaFiltersValues.criteriaStatusFilter,
      setTableSettings,
      setEvaluationCriteriaFilters,
    ],
  )

  const handleResetFilters = useCallback(() => {
    setEvaluationCriteriaFilters(defaultEvaluationCriteriaFiltersValues)
    setTableSettings({
      ...tableSettings,
      paginationModel: { ...tableSettings.paginationModel, page: 0 },
    })
  }, [defaultEvaluationCriteriaFiltersValues, tableSettings, setEvaluationCriteriaFilters, setTableSettings])

  // delete modal handlers
  const handleCancelDelete = useCallback(() => {
    setCriteriaToBeDeleted(null)
  }, [])

  const handleDeleteConfirmed = useCallback(() => {
    if (!criteriaToBeDeleted) {
      notificationService.operationFailed()
      return
    }

    setDeleteModalLoading(true)
    deleteEvaluationCriteria(urqlClient)(criteriaToBeDeleted)
      .then(() => {
        notificationService.deleteSuccessful()
        initRows()
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
      .finally(() => {
        setDeleteModalLoading(false)
        setCriteriaToBeDeleted(null)
      })
  }, [criteriaToBeDeleted, initRows, notificationService, urqlClient])

  useEffect(() => {
    const processesOfApplicationTypesToFetch = CRITERIA_CONFIG_PROCESSES.filter(
      (process) => process !== PROCESS.FACTSHEET,
    )

    fetchApplicationTypesByProcesses(urqlClient)(processesOfApplicationTypesToFetch)
      .then((res) => {
        setAllApplicationTypes(res.fetchedApplicationTypes)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [notificationService, urqlClient])

  useEffect(() => {
    initRows()
  }, [initRows])

  return (
    <>
      <ScreenLayout
        title={`${getMessage('label.navigation.configuration.criteria')} ${getMessage('label.configuration')}`}
      >
        <PageLayout>
          <Stack spacing={5}>
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 1,
                padding: 2,
              }}
            >
              <Stack spacing={2}>
                <Typography variant="body1" color="text.secondary" fontWeight="bold">
                  {getMessage('label.grid.filter')}
                </Typography>
                <Grid2 container spacing={2}>
                  <Grid2 size={3}>
                    <Autocomplete
                      options={criteriaProcessOptions}
                      value={evaluationCriteriaFilters.criteriaProcessFilter}
                      onChange={(_, newVal) => onProcessFilterChange(newVal)}
                      getOptionLabel={(opt) => getTranslationForProcess(getMessage, opt.value)}
                      renderInput={(params) => <TextField {...params} label={getMessage('label.process')} />}
                    />
                  </Grid2>

                  <Grid2 size={3}>
                    <Autocomplete
                      options={currentApplicationTypeOptions}
                      value={evaluationCriteriaFilters.criteriaApplicationTypeFilter}
                      onChange={(_, newVal) => onApplicationTypeFilterChange(newVal)}
                      getOptionLabel={(opt) =>
                        getTranslationForApplicationType(getMessage, opt.value, currentApplicationTypeOptions)
                      }
                      renderInput={(params) => <TextField {...params} label={getMessage('label.application_type')} />}
                    />
                  </Grid2>

                  <Grid2 size={3}>
                    <Autocomplete
                      options={criteriaTypeOptions}
                      value={evaluationCriteriaFilters.criteriaTypeFilter}
                      onChange={(_, newVal) => onCriteriaTypeFilterChange(newVal)}
                      getOptionLabel={(opt) => getTranslationForCriteriaType(getMessage, opt.value)}
                      renderInput={(params) => <TextField {...params} label={getMessage('label.criteria_type')} />}
                    />
                  </Grid2>

                  <Grid2 size={3}>
                    <Autocomplete
                      options={criteriaStatusOptions}
                      value={evaluationCriteriaFilters.criteriaStatusFilter}
                      onChange={(_, newVal) => onCriteriaStatusChange(newVal)}
                      getOptionLabel={(opt) => getTranslationForStatus(getMessage, opt.value)}
                      renderInput={(params) => <TextField {...params} label={getMessage('label.status')} />}
                    />
                  </Grid2>

                  <Grid2 size={4}>
                    <Button variant="text" startIcon={<FilterAltOffOutlinedIcon />} onClick={handleResetFilters}>
                      {getMessage('label.configuration.criteria.group.grid.filter.reset.button')}
                    </Button>
                  </Grid2>
                </Grid2>
              </Stack>
            </Box>
            <EvaluationCriteriaGrid
              dataSettings={gridDataSettings}
              tableSettings={tableSettings}
              setTableSettings={setTableSettings}
              setCriteriaToBeDeleted={setCriteriaToBeDeleted}
            />
          </Stack>
        </PageLayout>
      </ScreenLayout>

      <SecondaryConfirmationModalDialog
        open={!!criteriaToBeDeleted}
        onCancel={handleCancelDelete}
        onConfirm={handleDeleteConfirmed}
        loading={deleteModalLoading}
        buttonColor="error"
      >
        <Box paddingRight={1}>
          <Typography variant="subtitle1">
            {getMessage('label.configuration.evaluation.criteria.grid.delete.confirm.body')}
          </Typography>
        </Box>
      </SecondaryConfirmationModalDialog>
    </>
  )
}
